export const strategicCareerPlanningModule = {
  id: "ca-strategic-career-planning",
  title: "Strategic Career Planning",
  description: "Develop a comprehensive career strategy that aligns your skills, interests, and market opportunities for long-term success.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Conduct comprehensive career assessments to identify your strengths, values, and growth opportunities
- Create strategic career plans that balance personal fulfillment with market realities
- Develop skill portfolios that remain relevant as industries evolve
- Build personal brands that communicate your unique value proposition effectively

## Prerequisites
- Reflection on your current career satisfaction and goals
- Access to your performance reviews and feedback from the past 2-3 years

## Overview
Strategic career planning is the difference between reactive career moves and intentional professional growth. Professionals with clear career strategies earn 20% more on average and report 60% higher job satisfaction. This module provides frameworks used by executive coaches and career strategists to design fulfilling, sustainable career trajectories.

---

## Key Concepts

### The Career GPS Framework
Like any journey, career advancement requires knowing where you are, where you want to go, and the best route to get there. The Career GPS framework provides this navigation system.

#### Why It Matters
Professionals who use strategic career planning frameworks are 3x more likely to achieve their professional goals and 70% less likely to experience career plateaus. Clear direction enables focused effort and better decision-making.

#### Best Practices
- Conduct annual career audits to assess progress and adjust direction
- Set both outcome goals (salary, title) and process goals (skills, experiences)
- Create multiple career scenarios to maintain flexibility as markets change
- Align career moves with your core values and lifestyle priorities

### The T-Shaped Professional Model
Modern careers require both deep expertise in one area and broad capabilities across multiple disciplines. This T-shaped skill portfolio provides both specialization and adaptability.

#### Industry Usage
Leading consulting firms and technology companies actively recruit T-shaped professionals because they can solve complex problems that require both deep knowledge and cross-functional thinking. These professionals advance faster and command higher compensation.

---

## Practical Examples

### Example 1: Career Vision Statement Template
Create a compelling 3-year career vision that guides decision-making.

**Framework:**
"By [target date], I will be [role/title] at [type of organization], where I use my expertise in [core competency] to [impact/value creation]. I will have developed [2-3 new skills] and be recognized for [specific achievements]. This role will provide [lifestyle elements] while positioning me for [next career stage]."

### Example 2: Skills Portfolio Assessment
Map your current capabilities against future market needs.

**Assessment Matrix:**
- **Core Expertise** (Deep): Your primary area of professional mastery
- **Adjacent Skills** (Moderate): Complementary capabilities that enhance your value
- **Emerging Competencies** (Basic): Future-focused skills you're developing
- **Foundation Skills** (Strong): Universal capabilities like communication, analysis

💡 **Pro Tip**: Use the "70-20-10" rule for skill development - 70% of growth comes from challenging work experiences, 20% from mentoring and feedback, 10% from formal training.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations benefit when employees take ownership of career development because it increases engagement, reduces turnover, and builds internal talent pipelines. Companies with strong career development cultures show 40% higher retention and 25% better performance.

### Popular Tools and Libraries
- **LinkedIn Learning**: Skill assessments and learning path recommendations
- **StrengthsFinder**: Talent identification and development framework
- **16Personalities**: Personality-based career guidance and team dynamics

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Skills-Based Planning | Adaptable, market-responsive, future-focused | Dynamic industries, career pivots, emerging fields |
| Role-Based Planning | Clear progression, established pathways, predictable | Traditional industries, institutional careers, specialized fields |

---

## Key Takeaways

✅ **Essential Points**:
- Career strategy requires honest assessment of current state and clear vision of desired future
- T-shaped skill portfolios provide both expertise and adaptability in changing markets
- Personal branding communicates your unique value proposition to key stakeholders
- Regular career reviews and adjustments keep you aligned with both personal goals and market opportunities

⚠️ **Common Pitfalls**:
- Planning careers based on current market conditions without considering future evolution
- Focusing solely on skill development without building visibility and relationships

## Next Steps
In the next module, you'll learn how to build the professional networks that can accelerate your career progression and open unexpected opportunities.

---
`,
  duration: "55 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Master career assessment techniques",
    "Create strategic development plans",
    "Build T-shaped skill portfolios",
    "Design personal brand strategies"
  ],
  keyTopics: [
    "Career Assessment",
    "Strategic Planning",
    "Skill Development",
    "Personal Branding",
    "Market Analysis"
  ]
};